/*
 * Copyright (c) 2023 NOVA Parks. All rights reserved.
 */

/* global console, document, Excel, Office */

/**
 * @typedef {Object} AccountData
 * AccountData class is a data storage object
 * It holds account data summaries for one account number
 */
class AccountData {
  /**
   * @param {string} description The account's description
   * @param {string} accountNumber The account number
   * @param {number} cash Total cash payment for the account
   * @param {number} check Total check payment for the account
   * @param {number} gift Total gift certificate payment for the account
   * @param {number} credit Total credit card payment for the account
   * @param {number} total Combined total of all payments for the account
   */
  constructor(description, accountNumber, cash, check, gift, credit, total) {
    /**
     * @property {string} sescription The account description
     * Taken from column A
     */
    this.description = description;

    /**
     * @property {string} accountNumber The account number
     * Taken from column C
     */
    this.accountNumber = accountNumber;

    /**
     * @property {number} cash Total cash payment
     * Taken from column D
     */
    this.cash = cash;

    /**
     * @property {number} check Total check payment
     * Taken from column F
     */
    this.check = check;

    /**
     * @property {number} gift Total gift certificate payment
     * Taken from column G
     */
    this.gift = gift;

    /**
     * @property {number} credit Total credit card payment
     * Taken from column H
     */
    this.credit = credit;

    /**
     * @property {number} total Combined total of all payments
     * Taken from column M
     */
    this.total = total;
  }

  toData() {
    return {
      description: this.description,
      accountNumber: this.accountNumber,
      cash: this.cash,
      check: this.check,
      gift: this.gift,
      credit: this.credit,
      total: this.total,
    };
  }
}

/**
 * @typedef {Object} CashDistribution
 * CashDistribution is a storage object
 * It holds all the extracted data from the spreadsheet
 */
class CashDistribution {
  /**
   * @param {string} site The transaction site taken from cell F3
   * @param {string} date The date of the transactions
   * @param  {AccountData[]} accounts AccountData object associated with relevant line items in the sheet
   */
  constructor(site, date, accounts) {
    /**
     * @property {string} site The site this data applies to
     */
    this.site = site;

    /**
     * @property {string} date The date of transactions that the sheet is reporting
     */
    this.date = date;

    /**
     * @property {AccountData[]} accounts The associated account data extracted from the sheet
     */
    this.accounts = accounts;
  }

  toData() {
    let acnts = [];
    this.accounts.forEach((account) => {
      acnts.push(account.toData());
    });

    return {
      site: this.site,
      date: this.date,
      accounts: acnts,
    };
  }
}

// Finders - Find areas of the spreadsheet where information lives

/**
 * Scans the provided column for any cells containing an account number.
 * Will ignore blank cells and any cell containing "Account Number"
 * Looping terminates when "Totals:" is detected
 * Returns an array of rows where financial data is located
 * @param {string} col The column to loop through for account numbers
 * @returns {number[]} An array of rows that contain account numbers and therefore financial data
 */
const findRowsWithAccounts = async (col) => {
  let rowsContainingAccounts = [];

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();

    for (let i = 1; i < 999; i++) {
      let cell = col + i;
      let range = sheet.getRange(cell);

      range.load(["address", "values"]);
      // eslint-disable-next-line office-addins/no-context-sync-in-loop
      await context.sync();

      const cellValue = range.values[0][0] + "";

      if (cellValue == "") {
        continue;
      } // Ignore empty cells

      if (cellValue == "Account Number") {
        continue;
      } // Ignore the Account Number title

      if (cellValue == "Totals:") {
        // Add totals to the array and stop iterating
        rowsContainingAccounts.push(i);
        break;
      }

      const regexFormatted = "[0-9]{1}[-][0-9]{3}[-][0-9]{4}[-][0-9]{3}"; // Matches X-XXX-XXXX-XXX where X is a single numeric character 0-9
      const regexUnformatted = "[0-9]{11}"; // Matches XXXXXXXXXXX where X is a single numeric character. Necessary because some account numbers are unformatted.
      if (cellValue.match(regexFormatted)) {
        rowsContainingAccounts.push(i);
      } else if (cellValue.match(regexUnformatted)) {
        //! If this gets hit, then the account number value in the cell is formatted as a number by Excel
        //! We must convert it to a string and use the string value before manipulating it
        rowsContainingAccounts.push(i);
      }

      console.debug(`Range of ${range.address} contains value: ${cellValue}`);
    }
  }).catch(handleError);

  console.debug(`Found ${rowsContainingAccounts.length} rows containing data: ${rowsContainingAccounts.map((x) => x)}`);

  return rowsContainingAccounts;
};

/**
 * Returns the cell that contains the transaction site
 * Currently static. Transaction site appears to be in the same location every time
 * @returns {string} The cell address for the transaction site label
 */
const findTransactionSite = async () => {
  return "F3";
};

/**
 * Returns the cell that contains the transaction date
 * Currently static. Transaction date appears to be in the same location every time
 * @returns {string} The cell address for the transaction date label
 */
const findTransactionDate = async () => {
  return "F4";
};

// Getters - Retrieve information from the requisite areas of the spreadsheet

/**
 * Gets account data from the given array of row numbers
 * Turns it into an array of AccountData objects
 * @param {number[]} accountRows
 * @returns {AccountData[]} All rows of account data
 */
const getAccounts = async (accountRows) => {
  let accounts = [];

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();

    for (const row of accountRows) {
      // Index... In, Object... Of
      // I hate hard-coding these but they aren't liable to change
      // and making them dynamic is more challenging than it is currently worth.
      const descriptionCell = "A" + row;
      const accountCell = "C" + row;
      const cashCell = "D" + row;
      const checkCell = "F" + row;
      const giftCell = "G" + row;
      const creditCell = "H" + row;
      const totalCell = "M" + row;

      const descriptionRange = sheet.getRange(descriptionCell);
      const accountRange = sheet.getRange(accountCell);
      const cashRange = sheet.getRange(cashCell);
      const checkRange = sheet.getRange(checkCell);
      const giftRange = sheet.getRange(giftCell);
      const creditRange = sheet.getRange(creditCell);
      const totalRange = sheet.getRange(totalCell);

      descriptionRange.load("values");
      accountRange.load("values");
      cashRange.load("values");
      checkRange.load("values");
      giftRange.load("values");
      creditRange.load("values");
      totalRange.load("values");

      // eslint-disable-next-line office-addins/no-context-sync-in-loop
      await context.sync();

      let account = new AccountData();
      account.description = descriptionRange.values[0][0];
      account.accountNumber = accountRange.values[0][0];
      account.cash = cashRange.values[0][0];
      account.check = checkRange.values[0][0];
      account.gift = giftRange.values[0][0];
      account.credit = creditRange.values[0][0];
      account.total = totalRange.values[0][0];

      accounts.push(account);
    }
  }).catch(handleError);

  return accounts;
};

/**
 * Extract the transaction site from the cell text
 * Format:
 * Transaction Site: <transaction_site>
 * @param {string} siteCell The cell that contains the transaction site
 * @returns {string} The transaction site
 */
const getTransactionSite = async (siteCell) => {
  let site;

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();

    const rawSite = sheet.getRange(siteCell).load();
    await context.sync();

    site = rawSite.values[0][0].split(": ")[1];
  }).catch(handleError);

  return site;
};

/**
 * Extract the transaction date from the cell text
 * Format:
 * Transaction Date/Time: From <date> <time> <am/pm> through <date> <time> <am/pm>
 * We only care about the <date>
 * @param {string} dateCell the cell that contains the transaction date
 * @returns {string} The transaction date
 */
const getTransactionDate = async (dateCell) => {
  let date;

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();

    const rawDate = sheet.getRange(dateCell).load();
    await context.sync();

    let splitDate = rawDate.values[0][0].split(" ");
    date = `${splitDate[3]} ${splitDate[4]} ${splitDate[5]}`;
  }).catch(handleError);

  return date;
};

// Aggregators - Put all the data together into data object and ultimately JSON
const aggregate = async (site, date, accounts) => {
  return new CashDistribution(site, date, accounts);
};

/**
 * Creates a new sheet in the workbook formatted for Dynamics' copy + paste import
 * @param {CashDistribution} cashDist The financial data that needs to be populated in the upload sheet
 * @returns {undefined} TBD
 */
const createUploadSheet = async (cashDist) => {
  // Create a new sheet and populate the following headers:
  // Description - Account - Debit - Credit

  await Excel.run(async (context) => {
    const sheetName = "UploadSheet";

    let sheets = context.workbook.worksheets;
    let sheet = sheets.add(sheetName);

    // populate the new sheet with compiled data
    let headers = [["Description", "Account", "Debit", "Credit"]];
    let range = sheet.getRange("A1:D1");
    range.values = headers;

    await context.sync();

    for (let i = 0; i < cashDist.accounts.length; i++) {
      const account = cashDist.accounts[i];

      //* Credit: account.total
      //* Debit: account.cash + account.check + account.gift + account.credit

      //let debit, credit, data; // Variables for UploadSheet values

      if (account.accountNumber == "Totals:") {
        // add the totals as a Total Cash Payment row. Goes into Debit instead of credit
        // let cashDebit = account.cash + account.check == 0 ? "" : account.cash + account.check;
        // let giftDebit = account.gift == 0 ? "" : account.gift;
        // let creditDebit = account.credit == 0 ? "" : account.credit;
        let cashDebit,
          giftDebit,
          creditDebit,
          cashCredit,
          giftCredit,
          creditCredit = "";

        if (account.cash + account.check < 0) {
          cashCredit = Math.abs(account.cash + account.check);
        } else if (account.cash + account.check > 0) {
          cashDebit = account.cash + account.check;
        } else {
          // Do nothing
        }

        if (account.gift < 0) {
          giftCredit = Math.abs(account.gift);
        } else if (account.gift > 0) {
          giftDebit = account.gift;
        } else {
          // Do nothing
        }

        if (account.credit < 0) {
          creditCredit = Math.abs(account.credit);
        } else if (account.credit > 0) {
          creditDebit = account.credit;
        } else {
          // Do nothing
        }

        let data = [
          /*
           * Total Cash Payment and total Check payment use 1-000-1100-000
           * Total Gift Certificate payment use 3-000-2413-000
           * Total Credit Card payment use 1-000-1130-000
           */
          ["Total Cash/Check Payment", "1-000-1100-000", cashDebit, cashCredit],
          ["Total Credit Payment", "1-000-1130-000", creditDebit, creditCredit],
          ["Total Gift Certificate Payment", "3-000-2413-000", giftDebit, giftCredit],
        ];

        let row = i + 2; // +1 for 0 based array, +1 for real row number (Because of title row)
        let range = `A${row}:D${row + 2}`;

        range = sheet.getRange(range);
        range.values = data;
      } else {
        // Normal numbers here or something
        // If the balance on the account is negative, it goes into debit.
        let debit = "";
        let credit = "";

        if (account.total > 0) {
          credit = account.total;
        } else if (account.total < 0) {
          debit = Math.abs(account.total);
        } else {
          // It's a zero balance account, no value, no change.
        }

        let data = [[account.description, account.accountNumber, debit, credit]];

        let row = i + 2; // +1 for 0 based array, +1 for real row number
        let range = `A${row}:D${row}`;

        range = sheet.getRange(range);
        range.values = data;
      }
    }

    sheet.activate();
    sheet.getUsedRange().format.autofitColumns();

    await context.sync();
  });
};

Office.onReady((info) => {
  if (info.host === Office.HostType.Excel) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "flex";
    document.getElementById("run").onclick = compileData;
  }
});

export async function compileData(event) {
  console.debug("Running...");

  try {
    await Excel.run(async (/*context*/) => {
      // Run the finders, await the results
      const [addrAccounts, addrSite, addrDate] = await Promise.all([
        findRowsWithAccounts("C"),
        findTransactionSite(),
        findTransactionDate(),
      ]).catch(handleError);

      // Run the getters, await the results
      const [accounts, site, date] = await Promise.all([
        getAccounts(addrAccounts),
        getTransactionSite(addrSite),
        getTransactionDate(addrDate),
      ]).catch(handleError);

      // Aggregate the results
      const [summary] = await Promise.all([aggregate(site, date, accounts)]);

      console.debug(JSON.stringify(summary.toData()));

      await createUploadSheet(summary);
    });
  } catch (error) {
    console.error(error);
  }

  event.completed();
}

Office.actions.associate("compileData", compileData);

const handleError = async (error) => {
  console.error(error);
};
